import { Box } from "@material-ui/core";
import dayjs from "dayjs";
import * as React from "react";

import { MonthlyCashflowResult } from "@/api/generated";
import { CustomButton } from "@/components/Button";
import theme from "@/theme";
import { PropertyHolder } from "@/view_models/atoms";

import { useDeleteMonthlyCashflowResult } from "../../../hooks/useDeleteMonthlyCashflowResult";
import { EditPropertyHistoryItem } from "../../EditPropertyHistoryItem";

import { InsuranceInfoHistoryEditor } from "./HistoryEditor";
import { InsuranceType, getInsuranceTypeKeys } from "./types";

interface props {
  monthlyCashflowResult: MonthlyCashflowResult;
  insuranceType: InsuranceType;
  propertyHolder: PropertyHolder;
}

export const InsuranceInfoHistoryItem: React.FC<props> = ({ monthlyCashflowResult, insuranceType, propertyHolder }) => {
  const [isShowEdior, setIsShowEditor] = React.useState(false);
  const { deleteMonthlyCashflowResult } = useDeleteMonthlyCashflowResult();

  // 編集ボタンクリック時の処理
  const onClickEdit = React.useCallback(() => {
    setIsShowEditor(true);
  }, []);

  // 削除ボタンクリック時の処理
  const onClickDelete = React.useCallback(async () => {
    const fieldsToDelete = getInsuranceTypeKeys(insuranceType);
    const deleteFields = {};
    fieldsToDelete.forEach((field) => {
      deleteFields[field] = null;
    });

    await deleteMonthlyCashflowResult(monthlyCashflowResult.month, deleteFields);
  }, [insuranceType, monthlyCashflowResult.month, deleteMonthlyCashflowResult]);

  const getHistoryContents = (insuranceType: InsuranceType, data: MonthlyCashflowResult) => {
    const startMonth = dayjs(data.month).format("YYYY/MM");
    switch (insuranceType) {
      case "火災保険":
        if (!data.fire_insurance_cost && !data.fire_insurance_holding_period) return [];
        return [
          {
            label: "火災保険料",
            value: data.fire_insurance_cost ? `${data.fire_insurance_cost.toLocaleString()}円` : "-円",
          },
          {
            label: "火災保険期間",
            value: data.fire_insurance_holding_period
              ? `${startMonth}〜${dayjs(data.month).add(data.fire_insurance_holding_period, "year").format("YYYY/MM")}`
              : "-年/-月/-日 ~ -年/-月/-日",
          },
        ];
      case "地震保険":
        if (!data.earthquake_insurance_cost && !data.earthquake_insurance_holding_period) return [];
        return [
          {
            label: "地震保険料",
            value: data.earthquake_insurance_cost ? `${data.earthquake_insurance_cost.toLocaleString()}円` : "-円",
          },
          {
            label: "地震保険期間",
            value: data.earthquake_insurance_holding_period
              ? `${startMonth}〜${dayjs(data.month)
                  .add(data.earthquake_insurance_holding_period, "year")
                  .format("YYYY/MM")}`
              : "-年/-月/-日 ~ -年/-月/-日",
          },
        ];
      default:
        return [];
    }
  };

  if (getHistoryContents(insuranceType, monthlyCashflowResult).length === 0) {
    return null;
  }

  return (
    <Box>
      {isShowEdior ? (
        <InsuranceInfoHistoryEditor
          monthlyCashflowResult={monthlyCashflowResult}
          insuranceType={insuranceType}
          onCancel={() => setIsShowEditor(false)}
          onSave={() => setIsShowEditor(false)}
          propertyHolder={propertyHolder}
        />
      ) : (
        <EditPropertyHistoryItem
          contents={getHistoryContents(insuranceType, monthlyCashflowResult)}
          onEdit={onClickEdit}
          onDelete={onClickDelete}
        />
      )}
      {isShowEdior && (
        <CustomButton type="button" tone="text" onClick={onClickDelete} style={{ color: theme.palette.semantic["status/error-text"], marginTop: 16 }}>
          この記録を削除する
        </CustomButton>
      )}
    </Box>
  );
};
